// LLM-layer types. Kept independent of the @mlc-ai/web-llm package types so the
// rest of the codebase doesn't pull WebLLM into its bundle slice — only the
// client wrapper (which dynamic-imports the lib) sees those types.

export type EngineState =
  | { kind: 'absent'; reason: string }
  | { kind: 'idle'; modelId: string }
  | { kind: 'loading'; modelId: string; progress: number; text: string }
  | { kind: 'ready'; modelId: string; loadedAt: number; engineVersion: string }
  | { kind: 'error'; modelId: string; message: string };

export interface ChatRequest {
  systemPrompt: string;
  userPrompt: string;
  /** When set, response_format: json_object is requested. Parser validates. */
  schema?: object;
  maxTokens?: number;
  /** For audit-log attribution. */
  promptVersion: string;
}

export interface ChatResponse {
  /** Raw text from the model. */
  text: string;
  /** Parsed JSON when a schema was provided and parsing succeeded. */
  json?: unknown;
  /** Tokens used per the engine's report (when available). */
  tokens?: number;
}

export type ProgressCallback = (progress: number, text: string) => void;

/** Which engine backs LLM enrichment. `'on-device'` is the privacy default. */
export type LlmProvider = 'on-device' | 'gemini';

export interface LlmClient {
  getState(): EngineState;
  subscribe(listener: (state: EngineState) => void): () => void;
  /** Lazy-init or return existing engine. No-op when already ready. */
  ensureLoaded(modelId?: string): Promise<void>;
  chat(req: ChatRequest): Promise<ChatResponse>;
  /** Release engine + GPU memory. */
  unload(): Promise<void>;
}

// Default is the smaller model: ADR-0003 picked Llama-3.1-8B for quality, but
// in practice ~5-6 GB of VRAM exceeds typical editor hardware (integrated /
// modest GPUs) — it OOMs, the WebGPU device is lost, and enrichment never
// runs. A feature that doesn't run has no quality. Phi-3.5 Mini (~2.4 GB)
// fits constrained GPUs; capable machines can still pick Llama 8B in the
// model dropdown. See ADR-0003 amendment 2026-05-16.
export const DEFAULT_MODEL_ID = 'Phi-3.5-mini-instruct-q4f16_1-MLC';

export const SUPPORTED_MODELS: Array<{
  id: string;
  name: string;
  approxDownloadGB: number;
  contextK: number;
  note?: string;
}> = [
  {
    id: 'Phi-3.5-mini-instruct-q4f16_1-MLC',
    name: 'Phi-3.5 Mini Instruct',
    approxDownloadGB: 2.4,
    contextK: 4,
    note: 'Default — fits most GPUs (~2.4 GB). 4K context; best for typical hardware.',
  },
  {
    id: 'Llama-3.1-8B-Instruct-q4f16_1-MLC',
    name: 'Llama 3.1 8B Instruct',
    approxDownloadGB: 5.0,
    contextK: 128,
    note: 'Higher quality, 128K context — needs ~5-6 GB VRAM (capable GPUs only).',
  },
  {
    id: 'gemma-2-9b-it-q4f16_1-MLC',
    name: 'Gemma 2 9B Instruct',
    approxDownloadGB: 5.5,
    contextK: 8,
    note: 'Alternative 9B model — also needs a capable GPU.',
  },
];
